import { useState } from 'react'
import {
  Table, Group, Select, Title, Pagination, Badge, ActionIcon,
  Card, Text, Modal, Stack, Textarea, Button, Skeleton, CopyButton, Tooltip,
} from '@mantine/core'
import { IconEye, IconCheck, IconCopy } from '@tabler/icons-react'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'
import { useRoomBookings } from '@/lib/swr'
import { ROOM_BOOKING_STATUS_CONFIG } from '@/constants'
import type { RoomBooking } from '@/types'
import dayjs from 'dayjs'

const statusOptions = Object.entries(ROOM_BOOKING_STATUS_CONFIG).map(([k, v]) => ({ value: k, label: v.label }))
const statusFilterOptions = [{ value: 'all', label: 'Бүгд' }, ...statusOptions]

export default function BookingList() {
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const params: Record<string, string> = { page: String(page), limit: '20' }
  if (statusFilter && statusFilter !== 'all') params.status = statusFilter

  const { data, isLoading, mutate } = useRoomBookings(params)
  const bookings: RoomBooking[] = data?.data ?? []
  const pagination = (data as any)?.pagination

  const [selected, setSelected] = useState<RoomBooking | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)
  const [statusModal, setStatusModal] = useState(false)
  const [newStatus, setNewStatus] = useState('')
  const [cancelReason, setCancelReason] = useState('')
  const [saving, setSaving] = useState(false)

  const getRoomName = (b: RoomBooking) => typeof b.room === 'object' ? b.room.name : b.room
  const fmt = (d: string) => dayjs(d).format('YYYY.MM.DD')

  const openDetail = (b: RoomBooking) => { setSelected(b); setDetailOpen(true) }
  const openStatusChange = (b: RoomBooking) => {
    setSelected(b); setNewStatus(b.status); setCancelReason(''); setStatusModal(true)
  }

  const handleStatusChange = async () => {
    if (!selected) return
    setSaving(true)
    try {
      await api.admin.updateRoomBookingStatus(selected._id, newStatus, cancelReason || undefined)
      toast.success('Статус шинэчлэгдлээ')
      setStatusModal(false); mutate()
    } catch (err: any) {
      toast.error(err instanceof Error ? err.message : 'Алдаа гарлаа')
    } finally { setSaving(false) }
  }

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Group gap="sm">
          <Title order={2}>Захиалгууд</Title>
          {pagination?.total > 0 && <Badge variant="light" color="gray">{pagination.total}</Badge>}
        </Group>
        <Select data={statusFilterOptions} value={statusFilter} onChange={(v) => { setStatusFilter(v || 'all'); setPage(1) }} w={180} />
      </Group>

      <Card shadow="xs" padding={0} radius="md" withBorder>
        <Table.ScrollContainer minWidth={1000}>
          <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover>
            <Table.Thead>
              <Table.Tr style={{ backgroundColor: 'var(--app-table-header)' }}>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700} w={50}>#</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Өвчтөн</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Утас</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Өрөө</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Ирэх</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Гарах</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Дүн</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Мэдээлэл</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Статус</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700} w={60}></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <Table.Tr key={i}>
                    {Array.from({ length: 10 }).map((_, j) => <Table.Td key={j}><Skeleton h={16} /></Table.Td>)}
                  </Table.Tr>
                ))
              ) : (
                <>
                  {bookings.map((b, i) => {
                    const cfg = ROOM_BOOKING_STATUS_CONFIG[b.status]
                    return (
                      <Table.Tr key={b._id}>
                        <Table.Td c="dimmed" fz="sm">{(page - 1) * 20 + i + 1}</Table.Td>
                        <Table.Td>
                          {b.patientName ? (
                            <>
                              <Text size="sm" fw={500}>{b.patientName}</Text>
                              <Text size="xs" c="dimmed">{b.registerNo}</Text>
                            </>
                          ) : (
                            <Text size="xs" c="dimmed" fs="italic">Бөглөөгүй</Text>
                          )}
                        </Table.Td>
                        <Table.Td><Text size="sm">{b.phone}</Text></Table.Td>
                        <Table.Td><Text size="sm">{getRoomName(b)}</Text></Table.Td>
                        <Table.Td><Text size="sm" c="dimmed">{fmt(b.checkIn)}</Text></Table.Td>
                        <Table.Td><Text size="sm" c="dimmed">{fmt(b.checkOut)}</Text></Table.Td>
                        <Table.Td><Text size="sm" fw={500}>{b.totalAmount.toLocaleString()}₮</Text></Table.Td>
                        <Table.Td>
                          <Badge variant="light" size="sm" color={b.infoCompleted ? 'green' : 'orange'}>
                            {b.infoCompleted ? '✓ Бүрэн' : '✗ Дутуу'}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Badge variant="light" color={cfg?.color || 'gray'} size="sm" style={{ cursor: 'pointer' }} onClick={() => openStatusChange(b)}>
                            {cfg?.label || b.status}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <ActionIcon variant="light" color="blue" radius="md" onClick={() => openDetail(b)}>
                            <IconEye size={16} />
                          </ActionIcon>
                        </Table.Td>
                      </Table.Tr>
                    )
                  })}
                  {bookings.length === 0 && !isLoading && (
                    <Table.Tr><Table.Td colSpan={10}><Text ta="center" c="dimmed" py="xl">Захиалга олдсонгүй</Text></Table.Td></Table.Tr>
                  )}
                </>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
        {pagination && pagination.pages > 1 && (
          <Group justify="center" p="md" style={{ borderTop: '1px solid var(--app-border)' }}>
            <Pagination total={pagination.pages} value={page} onChange={setPage} />
          </Group>
        )}
      </Card>

      {/* Detail modal */}
      <Modal opened={detailOpen} onClose={() => setDetailOpen(false)} title="Захиалгын дэлгэрэнгүй" centered size="md">
        {selected && (
          <Stack gap="sm">
            <Group grow>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Өвчтөний нэр</Text>
                <Text size="sm" fw={500}>{selected.patientName || '—'}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Регистр</Text>
                <Text size="sm">{selected.registerNo || '—'}</Text>
              </div>
            </Group>
            <Group grow>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Утас</Text>
                <Text size="sm">{selected.phone}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Өрөө</Text>
                <Text size="sm">{getRoomName(selected)}</Text>
              </div>
            </Group>
            <Group grow>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Ирэх</Text>
                <Text size="sm">{fmt(selected.checkIn)}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Гарах</Text>
                <Text size="sm">{fmt(selected.checkOut)}</Text>
              </div>
            </Group>
            <Group grow>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Нийт дүн</Text>
                <Text size="sm" fw={600}>{selected.totalAmount.toLocaleString()}₮</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Урьдчилгаа</Text>
                <Text size="sm">{selected.prepaymentAmount.toLocaleString()}₮</Text>
              </div>
            </Group>
            {selected.guardian && (
              <Group grow>
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Асран хамгаалагч</Text>
                  <Text size="sm">{selected.guardian}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Утас</Text>
                  <Text size="sm">{selected.guardianPhone || '—'}</Text>
                </div>
              </Group>
            )}
            <Group grow>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Статус</Text>
                <Badge color={ROOM_BOOKING_STATUS_CONFIG[selected.status]?.color || 'gray'} variant="light">
                  {ROOM_BOOKING_STATUS_CONFIG[selected.status]?.label || selected.status}
                </Badge>
              </div>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Мэдээлэл</Text>
                <Badge color={selected.infoCompleted ? 'green' : 'orange'} variant="light">
                  {selected.infoCompleted ? 'Бүрэн бөглөгдсөн' : 'Дутуу'}
                </Badge>
              </div>
            </Group>
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={700} mb={4}>Захиалгын код</Text>
              <Group gap="xs">
                <Text size="sm" ff="monospace" fw={600}>{selected.shortCode}</Text>
                <CopyButton value={`/invoice/${selected.shortCode}`}>
                  {({ copied, copy }) => (
                    <Tooltip label={copied ? 'Хуулагдлаа' : 'Холбоос хуулах'}>
                      <ActionIcon size="xs" variant="light" color={copied ? 'green' : 'gray'} onClick={copy}>
                        {copied ? <IconCheck size={12} /> : <IconCopy size={12} />}
                      </ActionIcon>
                    </Tooltip>
                  )}
                </CopyButton>
              </Group>
            </div>
            <Text size="xs" c="dimmed">Огноо: {dayjs(selected.createdAt).format('YYYY.MM.DD HH:mm')}</Text>
          </Stack>
        )}
      </Modal>

      {/* Status change modal */}
      <Modal opened={statusModal} onClose={() => setStatusModal(false)} title="Статус өөрчлөх" centered size="sm">
        <Stack>
          <Select label="Статус" data={statusOptions} value={newStatus} onChange={(v) => setNewStatus(v || 'PENDING')} />
          {newStatus === 'CANCELED' && (
            <Textarea label="Цуцалсан шалтгаан" value={cancelReason} onChange={(e) => setCancelReason(e.currentTarget.value)} />
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setStatusModal(false)}>Болих</Button>
            <Button onClick={handleStatusChange} loading={saving}>Хадгалах</Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  )
}
